/**
 * Con lo que el técnico abre la app: lo que tiene que hacer.
 *
 * ── Por qué está separado en «atrasado / hoy / después» ──
 *
 * Una lista plana de trabajos pendientes no dice por dónde empezar. Un trabajo programado
 * para antier y uno para la otra semana se ven igual, y el de antier es el que tiene un
 * cliente llamando. La pantalla lo dice ella misma en vez de dejar que el técnico lo
 * averigüe leyendo fechas.
 *
 * Los que no tienen fecha van con «después» y no se pierden: en este sistema muchos tickets
 * entran sin programar y una pantalla que solo enseñara lo agendado dejaría trabajo fuera.
 */
import React from 'react';
import { COLOR, PRIORIDAD, ESTADO, TIPO, s } from './estilos';
import { haceCuanto } from './guardado';

const hora = (fecha) => fecha
  ? new Date(fecha).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })
  : null;

const fechaCorta = (fecha) => fecha
  ? new Date(fecha).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })
  : null;

/**
 * De quién es este trabajo.
 *
 * Desde que todos los técnicos ven todos los tickets (Víctor, 29-sep-2026), lo primero que
 * necesita saber el que mira la lista es si alguien ya lo cogió. Sin esta etiqueta, dos
 * técnicos salen a la misma casa.
 *
 * «Libre» se dice en verde y no en gris a propósito: no es una carencia, es el trabajo que
 * se puede tomar.
 */
function DeQuien({ t }) {
  if (t.es_mio) {
    return <span style={{ ...s.chip, background: `${COLOR.acento}22`, color: COLOR.acento, fontSize: '11px' }}>
      👤 Tuyo
    </span>;
  }
  if (t.tecnico_nombre) {
    return <span style={{ ...s.chip, background: 'rgba(255,255,255,0.06)', color: COLOR.suave, fontSize: '11px' }}>
      🔧 {String(t.tecnico_nombre).split(' ')[0]}
    </span>;
  }
  return <span style={{ ...s.chip, background: `${COLOR.verde}22`, color: COLOR.verde, fontSize: '11px' }}>
    ✋ Libre
  </span>;
}

function Tarjeta({ t, onAbrir }) {
  const pCfg = PRIORIDAD[t.prioridad] || PRIORIDAD.normal;
  const eCfg = ESTADO[t.estado] || ESTADO.abierto;
  return (
    <button
      onClick={() => onAbrir(t.id)}
      /* La tarjeta es toda texto suelto dentro de un botón, así que sin esto el botón no
         tiene nombre: quien use el lector de pantalla oye «botón» cuatro veces seguidas. */
      aria-label={`Abrir el trabajo ${t.titulo}${t.cliente_nombre ? ', del cliente ' + t.cliente_nombre : ''}`
        + (t.es_mio ? ', asignado a usted' : t.tecnico_nombre ? `, lo tiene ${t.tecnico_nombre}` : ', libre')}
      style={{
        ...s.tarjeta,
        textAlign: 'left', cursor: 'pointer', width: '100%',
        display: 'grid', gridTemplateColumns: '4px minmax(0,1fr)', gap: '12px',
        padding: 0, overflow: 'hidden', color: COLOR.texto,
      }}
    >
      {/* La barrita de color dice la prioridad sin ocupar una línea de texto. */}
      <div style={{ background: pCfg.color }} />
      <div style={{ padding: '14px 14px 14px 0', minWidth: 0 }}>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
          <DeQuien t={t} />
          <span style={{ ...s.chip, background: `${eCfg.color}22`, color: eCfg.color, fontSize: '11px' }}>
            {eCfg.icono} {eCfg.label}
          </span>
          {t.prioridad === 'urgente' && (
            <span style={{ ...s.chip, background: `${COLOR.rojo}22`, color: COLOR.rojo, fontSize: '11px' }}>
              Urgente
            </span>
          )}
          <span style={{ ...s.chip, background: 'rgba(255,255,255,0.05)', color: COLOR.suave, fontSize: '11px' }}>
            {TIPO[t.tipo] || t.tipo}
          </span>
        </div>
        <div style={{ ...s.titulo, fontSize: '15px' }}>{t.titulo}</div>
        {t.cliente_nombre && (
          <div style={{ ...s.sub, marginTop: '4px' }}>👤 {t.cliente_nombre}</div>
        )}
        {t.cliente_direccion && (
          <div style={{ ...s.sub, marginTop: '2px' }}>📍 {t.cliente_direccion}</div>
        )}
        {t.fecha_programada && (
          <div style={{ ...s.sub, marginTop: '6px', color: t.atrasado ? COLOR.ambar : COLOR.suave }}>
            🗓️ {t.es_de_hoy ? `Hoy ${hora(t.fecha_programada)}` : `${fechaCorta(t.fecha_programada)} · ${hora(t.fecha_programada)}`}
          </div>
        )}
      </div>
    </button>
  );
}

function Grupo({ titulo, color, tickets, onAbrir }) {
  if (!tickets.length) return null;
  return (
    <div style={{ display: 'grid', gap: '10px', minWidth: 0 }}>
      <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', color, textTransform: 'uppercase' }}>
        {titulo} · {tickets.length}
      </div>
      {tickets.map((t) => <Tarjeta key={t.id} t={t} onAbrir={onAbrir} />)}
    </div>
  );
}

export default function MiDia({ datos, hayRed, deGuardado, cargando, onAbrir, onRefrescar }) {
  const tickets = datos?.tickets || [];

  const atrasados = tickets.filter((t) => t.atrasado);
  const deHoy = tickets.filter((t) => t.es_de_hoy);
  const despues = tickets.filter((t) => !t.atrasado && !t.es_de_hoy);

  return (
    <div style={s.cuerpo}>
      {/* Lo primero de la pantalla dice de cuándo son los datos. Sin esto, una lista vieja se
          ve exactamente igual a una recién traída — y ahí es donde alguien va a una dirección
          que ya cambió. */}
      {deGuardado && (
        <div style={{ ...s.aviso, background: 'rgba(245,158,11,0.12)', color: '#FCD34D' }}>
          <span>📵</span>
          <span>
            Sin señal. Esto es lo que se trajo {haceCuanto(datos?.traido_en)}; puede que haya
            cambiado. No se puede guardar nada hasta que vuelva la señal.
          </span>
        </div>
      )}

      {cargando && !tickets.length && <div style={s.vacio}>Buscando su trabajo…</div>}

      {!cargando && !tickets.length && (
        <div style={s.vacio}>
          <div style={{ fontSize: '44px', marginBottom: '12px' }}>🎉</div>
          <div style={{ fontSize: '17px', fontWeight: 700, color: COLOR.texto, marginBottom: '6px' }}>
            No hay trabajos pendientes
          </div>
          <div>Aquí sale todo el trabajo del equipo. Cuando entre uno nuevo, aparece solo.</div>
        </div>
      )}

      <Grupo titulo="Atrasado" color={COLOR.ambar} tickets={atrasados} onAbrir={onAbrir} />
      <Grupo titulo="Para hoy" color={COLOR.acento} tickets={deHoy} onAbrir={onAbrir} />
      <Grupo titulo="Después" color={COLOR.suave} tickets={despues} onAbrir={onAbrir} />

      {!!tickets.length && (
        <button
          style={{ ...s.botonSuave, marginTop: '4px', opacity: hayRed ? 1 : 0.5 }}
          onClick={onRefrescar}
          disabled={!hayRed || cargando}
        >
          {cargando ? 'Actualizando…' : '↻ Actualizar'}
        </button>
      )}
    </div>
  );
}
